import { httpInterceptor } from '../http-interceptor';

export class ReceivingShipmentService {
  static ENDPOINTS = {
    CREATE: 'ReceivingShipment/create',
    UPDATE: 'ReceivingShipment/update',
    MERGE_POS: 'ReceivingShipment/merge-pos',
    DEMERGE_POS: 'ReceivingShipment/demerge-pos',
    UPLOAD_PO_DOCUMENT: 'ReceivingShipment/upload-po-document',
    UPLOAD_ONEDRIVE_DOCUMENT: 'ReceivingShipment/upload-onedrive-document',
    GET_SHIP_DOC: 'ReceivingShipment/shipdoc',
    GET_ALL: 'ReceivingShipment/getallreceivingshipments',
    GET_BY_ID: 'ReceivingShipment',
    REMOVE_PO: 'ReceivingShipment/remove-po',
    REPORT: 'ReceivingShipment/report',
    PICK_TICKETS: 'ReceivingShipment/pick-tickets',
    MARK_PICK_TICKET_PRINTED: 'ReceivingShipment/mark-pick-ticket-printed',
    SAVE_OSD_REPORT: 'ReceivingShipment/save-osd-report',
    GET_OSD_REPORT: 'ReceivingShipment/osd-report',
  };

  // Create a new receiving shipment
  static async createShipment(shipmentData) {
    return httpInterceptor.post(this.ENDPOINTS.CREATE, shipmentData);
  }

  // Update an existing receiving shipment (description/date)
  static async updateShipment(shipmentData) {
    return httpInterceptor.post(this.ENDPOINTS.UPDATE, shipmentData);
  }

  // Merge POs into a shipment
  static async mergePOs(mergeRequest) {
    return httpInterceptor.post(this.ENDPOINTS.MERGE_POS, mergeRequest);
  }

  // Upload PO document info (not the file itself)
  static async savePODocumentInfo(data) {
    return httpInterceptor.post(this.ENDPOINTS.UPLOAD_PO_DOCUMENT, data);
  }

  // Upload receiving shipment or PO document to OneDrive
  static async uploadOneDriveDocument(formData) {
    return httpInterceptor.postfile(this.ENDPOINTS.UPLOAD_ONEDRIVE_DOCUMENT, formData);
  }

  /**
   * Get OneDrive receiving document by shipment number, optionally a PO document.
   * Response.response: { exists, shipmentNumber, poNumber, fileName, fileBase64, webUrl }
   */
  static async getShipDoc(shipmentNumber, poNumber, includeContent = true) {
    const shipment = encodeURIComponent(String(shipmentNumber ?? '').trim());
    const params = {};
    if (poNumber) params.poNumber = String(poNumber).trim();
    if (!includeContent) params.includeContent = 'false';
    return httpInterceptor.get(`${this.ENDPOINTS.GET_SHIP_DOC}/${shipment}`, params);
  }

  // Get all shipments (optional filters: searchText, partNumber, poNumber, supplierName, status, shipmentDate)
  static async getAllShipments(filters = {}) {
    return httpInterceptor.post(this.ENDPOINTS.GET_ALL, filters || {});
  }

  // Get shipment by ID
  static async getShipmentById(id) {
    return httpInterceptor.get(`ReceivingShipment/${id}`);
  }

  // Remove PO from shipment
  static async removePOFromShipment(request) {
    return httpInterceptor.post(this.ENDPOINTS.REMOVE_PO, request);
  }

  // Save part receiving quantity
  static async savePartReceiving(data) {
    return httpInterceptor.post('ReceivingShipment/save-part-receiving', data);
  }

  // Get receiving shipment report
  static async getReport(data) {
    return httpInterceptor.post(this.ENDPOINTS.REPORT, data);
  }

  // Get shipments by PO, PO Item, or PO Release
  static async getShipmentsByPOQuery(payload) {
    return httpInterceptor.post('ReceivingShipment/get-shipments-by-po', payload);
  }

  // Demerge POs from a shipment
  static async deMergePOs(demergeRequest) {
    return httpInterceptor.post(this.ENDPOINTS.DEMERGE_POS, demergeRequest);
  }

  // Close a shipment by ID
  static async closeShipment(id) {
    return httpInterceptor.post(`ReceivingShipment/close-shipment/${id}`);
  }

  // Void an item (release or part)
  static async voidItem(data) {
    return httpInterceptor.post('ReceivingShipment/void-item', data);
  }

  static async getPickTickets(payload) {
    return httpInterceptor.post(this.ENDPOINTS.PICK_TICKETS, payload);
  }

  /** Mark pick ticket Printed after the label is actually printed (e.g. printer callback). */
  static async markPickTicketPrinted(payload) {
    return httpInterceptor.post(this.ENDPOINTS.MARK_PICK_TICKET_PRINTED, payload);
  }

  // Save an OS&D (Over, Short & Damaged) report for a receiving shipment
  static async saveOsdReport(payload) {
    return httpInterceptor.post(this.ENDPOINTS.SAVE_OSD_REPORT, payload);
  }

  // Get the most recently saved OS&D report for a shipment (response.response is null if none exists yet)
  static async getOsdReport(receivingShipmentId) {
    return httpInterceptor.get(`${this.ENDPOINTS.GET_OSD_REPORT}/${receivingShipmentId}`);
  }
}